<p-fluid>
    <div class="grid flex mb-5">
        <div class="col-12">
            <h1 class="mt-0 mb-0">Purchase Order Items</h1>
        </div>
    </div>
</p-fluid>

<div class="grid">
    <div class="col-12">
        <p-table #dt
                 [lazy]="true"
                 (onLazyLoad)="tableUpdated($event)"
                 [value]="purchaseOrderItems"
                 dataKey="id"
                 [rows]="itemsPerPage"
                 [filters]="tableFilters"
                 [loading]="loading"
                 [rowHover]="true"
                 styleClass="p-datatable-striped"
                 [paginator]="true"
                 currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Purchase Order Items"
                 [showCurrentPageReport]="true"
                 [totalRecords]="totalNbPurchaseOrderItems"
                 responsiveLayout="scroll"
                 [rowsPerPageOptions]="[10,20,25,50,75,100]"
                 sortMode="single"
                 editMode="row"
                 [reorderableColumns]="true"
                 (onColReorder)="onColReorder($event)"
        >
            <ng-template #caption>
                <p-multiSelect
                    [display]="selectedColumnDisplayMode"
                    [options]="cols"
                    [ngModel]="selectedColumns"
                    optionLabel="header"
                    [maxSelectedLabels]="maxSelectedColumnLabels"
                    selectedItemsLabel="{0} columns selected"
                    [style]="{ 'min-width': '200px' }"
                    placeholder="Choose Columns"
                    (onChange)="onColumnSelectChange($event)"
                />
            </ng-template>

            <ng-template pTemplate="header">
                <tr>
                    @for (column of selectedColumns; track column.field) {
                        @if (column.sortable) {
                            <th
                                [pSortableColumn]="column.field"
                                pReorderableColumn
                                [style]="{ 'white-space': 'nowrap' }"
                            >
                                {{ column.header }}
                                <p-sortIcon [field]="column.field"></p-sortIcon>
                            </th>
                        } @else {
                            <th
                                pReorderableColumn
                                [style]="{ 'white-space': 'nowrap' }"
                            >
                                {{ column.header }}
                            </th>
                        }
                    }
                </tr>

                <tr>
                    @for (column of selectedColumns; track column.field) {
                        <th [style]="{ 'white-space': 'nowrap' }">
                            @if (column.field === 'status') {
                                <p-columnFilter
                                    [field]="column.field"
                                    matchMode="in"
                                    [showMenu]="false"
                                >
                                    <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                                        <p-multiSelect
                                            [ngModel]="value"
                                            [options]="purchaseOrderStatusOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            placeholder="Any"
                                            selectedItemsLabel="{0} statuses selected"
                                            [maxSelectedLabels]="1"
                                            [style]="{ 'min-width': '120px', 'max-width': '180px' }"
                                            (onChange)="filter($event.value)"
                                            appendTo="body"
                                        />
                                    </ng-template>
                                </p-columnFilter>
                            } @else if (column.field === 'manufacturer') {
                                <p-columnFilter
                                    [field]="column.field"
                                    matchMode="in"
                                    [showMenu]="false"
                                >
                                    <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                                        <p-multiSelect
                                            [ngModel]="value"
                                            [options]="manufacturers"
                                            optionLabel="name"
                                            optionValue="name"
                                            placeholder="Any"
                                            selectedItemsLabel="{0} manufacturers selected"
                                            [maxSelectedLabels]="1"
                                            (onChange)="filter($event.value)"
                                            appendTo="body"
                                        />
                                    </ng-template>
                                </p-columnFilter>
                            } @else if (column.type === 'date') {
                                <p-columnFilter
                                    [field]="column.field"
                                    [showMenu]="false"
                                >
                                    <ng-template pTemplate="filter" let-value let-filter="filterCallback">
                                        <div class="flex gap-2 align-items-center">
                                            <input
                                                #fromDate
                                                type="date"
                                                class="p-inputtext p-component"
                                                [value]="getDateRangeInputValue(value, 'from')"
                                                [style]="{ 'width': '8.5rem' }"
                                                (change)="onDateRangeFromChange(fromDate.value, value, filter)"
                                            />

                                            <span>to</span>

                                            <input
                                                #toDate
                                                type="date"
                                                class="p-inputtext p-component"
                                                [value]="getDateRangeInputValue(value, 'to')"
                                                [style]="{ 'width': '8.5rem' }"
                                                (change)="onDateRangeToChange(toDate.value, value, filter)"
                                            />
                                        </div>
                                    </ng-template>
                                </p-columnFilter>
                            } @else if (column.type === 'currency') {
                                <p-columnFilter
                                    type="numeric"
                                    [field]="column.field"
                                    currency="EUR"
                                    [showMenu]="false">
                                </p-columnFilter>
                            } @else if (column.type === 'number') {
                                <p-columnFilter
                                    type="numeric"
                                    [field]="column.field"
                                    [showMenu]="false">
                                </p-columnFilter>
                            } @else {
                                <p-columnFilter
                                    type="text"
                                    [field]="column.field"
                                    [showMenu]="false">
                                </p-columnFilter>
                            }
                        </th>
                    }
                </tr>
            </ng-template>

            <ng-template pTemplate="body" let-purchaseOrderItem let-editing="editing" let-ri="rowIndex">
                <tr [pEditableRow]="purchaseOrderItem">
                    @for (column of selectedColumns; track column.field) {
                        @if (column.type === 'date') {
                            <td [style]="{ 'white-space': 'nowrap' }">
                                {{ getDateCellValue(purchaseOrderItem, column) | date: 'd. MMM, y' }}
                            </td>
                        } @else if (column.type === 'currency') {
                            @if (column.field === 'euroPerWatt') {
                                <td [style]="{ 'white-space': 'nowrap' }">
                                    {{ getCurrencyCellValue(purchaseOrderItem, column) | currency: 'EUR' : 'symbol-narrow' : '1.0-3' }}
                                </td>
                            } @else {
                                <td [style]="{ 'white-space': 'nowrap' }">
                                    {{ getCurrencyCellValue(purchaseOrderItem, column) | currency: 'EUR' : 'symbol-narrow' : '1.0-2' }}
                                </td>
                            }
                        } @else {
                            <td [style]="{ 'white-space': 'nowrap' }">
                                {{ getTextCellValue(purchaseOrderItem, column) }}
                            </td>
                        }
                    }
                </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
                <tr>
                    <td
                        [attr.colspan]="selectedColumns.length || 1"
                        [style]="{ 'white-space': 'nowrap' }"
                    >
                        No Purchase Order Items found.
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
</div>
